import React from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

import { NEWLY_CONNECTED_PRODUCTS_EXPIRY_MS } from '../../constants/3p-bulk-connect';
import { type ProductKeys, type ProductKeys3P, useProductConfigs } from '../../constants/products';
import type { ThirdPartyConfigsBootstrap } from '../../constants/schemas/3p-config';
import { useBulkConnectBanner } from '../../utils/bulk-connect-banner';
import { BannerType } from '../../utils/bulk-connect-banner/types';
import { onBannerDismissed } from '../../utils/events/banner-dismissed';
import { OAuthSource } from '../../utils/events/oauth-events';
import { BulkConnectBannerAnalyticsWrapper } from '../../utils/quick-find/analytics';
import { onBulkConenctModalShown } from '../../utils/quick-find/events/bulk-connect-modal-shown';
import { OAuthMessage } from '../oauth-message';

import { getBannerContent } from './utils';

const bulkConnectBannerContainerStyles = xcss({
	paddingBottom: 'space.075',
	paddingLeft: 'space.100',
	paddingRight: 'space.100',
});

type BulkConnectBannerProps = {
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	availableProducts: ProductKeys[];
	getNewlyConnectedProducts: (expiryDuration?: number) => ProductKeys3P[];
	isOnSearchDialog?: boolean;
	useIsBulkConnectModalOpenQuickFind?: () => [
		boolean,
		{ setIsBulkConnectModalOpen: (value: boolean) => void },
	]; // this pertains to bulk connect modal instance on quickfind. It was necessary to prevent QF (and hence, the modal) from closing when clicking while the modal is open
	useBulkConnectModalOpenState?: () => {
		isBulkConnectModalOpen: boolean;
		setIsBulkConnectModalOpen: (status: boolean) => void;
	}; // this pertains to bulk connect modal instance on full page search. It was necessary to prevent the modal from closing upon successful OAuth connection
	// Analytics props
	fireAnalyticsScreenEvent: (payload: any) => void;
	fireAnalyticsEvent: (payload: any) => void;
	commonAttributes: Record<string, any>;
};

/**
 * BulkConnectBanner component
 * This component displays a banner prompting users to connect their 3p products, and is the entrypoint to the modal dialog for connecting the products.
 */
export const BulkConnectBanner = ({
	thirdPartyConfigs,
	availableProducts,
	getNewlyConnectedProducts,
	isOnSearchDialog,
	useIsBulkConnectModalOpenQuickFind,
	useBulkConnectModalOpenState,
	fireAnalyticsScreenEvent,
	fireAnalyticsEvent,
	commonAttributes,
}: BulkConnectBannerProps): React.JSX.Element | null => {
	const allProducts = useProductConfigs();

	// Get Full Page Search modal state if available
	const fullPageSearchModalState = useBulkConnectModalOpenState?.() ?? {
		isBulkConnectModalOpen: false,
		setIsBulkConnectModalOpen: () => {},
	};

	// For QuickFind, use store state store/quick-find. For Full Page Search, use store/3p-bulk-connect
	const modalState = useIsBulkConnectModalOpenQuickFind?.() ?? [
		fullPageSearchModalState.isBulkConnectModalOpen,
		{ setIsBulkConnectModalOpen: fullPageSearchModalState.setIsBulkConnectModalOpen },
	];

	const [_, { setIsBulkConnectModalOpen }] = modalState;

	const newlyConnectedProductsWithExpiry = getNewlyConnectedProducts(
		NEWLY_CONNECTED_PRODUCTS_EXPIRY_MS,
	);
	const { bannerType, dismissBanner } = useBulkConnectBanner({
		thirdPartyConfigs,
		availableProducts,
		newlyConnectedProducts: newlyConnectedProductsWithExpiry,
	});

	if (bannerType === BannerType.NONE) {
		return null;
	}

	const handleAuthorize = () => {
		setIsBulkConnectModalOpen(true);
		fireAnalyticsScreenEvent(
			onBulkConenctModalShown({
				commonAttributes,
			}),
		);
	};

	const newlyConnectedProducts = getNewlyConnectedProducts();
	const messageContent = getBannerContent(newlyConnectedProducts, allProducts, bannerType);
	if (!messageContent) {
		return null;
	}

	const messageProps = {
		content: messageContent,
		isBulkConnect: true,
		onAuthorize: handleAuthorize,
		onDismiss: () => {
			fireAnalyticsEvent(
				onBannerDismissed({
					isOnSearchDialog,
					oAuthSource: isOnSearchDialog
						? OAuthSource.quickFindBulkConnect
						: OAuthSource.searchPageBulkConnect,
					componentName: 'BulkConnectBanner',
				}),
			);
			dismissBanner();
		},
	};

	return (
		<BulkConnectBannerAnalyticsWrapper>
			{isOnSearchDialog ? (
				<Box xcss={bulkConnectBannerContainerStyles}>
					<OAuthMessage
						{...messageProps}
						isOnSearchDialog
						oAuthSource={OAuthSource.quickFindBulkConnect}
					/>
				</Box>
			) : (
				<OAuthMessage {...messageProps} oAuthSource={OAuthSource.searchPageBulkConnect} />
			)}
		</BulkConnectBannerAnalyticsWrapper>
	);
};
